import {
  Button,
  Container,
  Fieldset,
  Text,
  Paper,
  Flex,
  Accordion,
} from "@mantine/core";
import { useForm } from "react-hook-form";
import { Checkbox, TextInput, Textarea } from "react-hook-form-mantine";
import { useParams } from "react-router-dom";
import { IconAlertCircle } from "@tabler/icons-react";

import { useGetUser, useUpdateUser } from "../../api/user";
import { useUpdateTicket } from "../../api/tickets";
import DeleteProfilePicture from "../Buttons/DeleteProfilePicture";
import { extractDirtyData } from "../../utils/dirtyData";
import { fullFormats } from "ajv-formats/dist/formats";
import ImageUpload from "../ImageUpload";
import UploadUserDocument from "../UserDocuments/UploadUserDocument";
import UserDocuments from "../UserDocuments/UserDocuments";
import { useContext } from "react";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { web_updateUserSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";

const EditUserWrapper = () => {
  const { id } = useParams();

  const { user: currentUser } = useContext(AuthContext);
  const currentWorkspaceId = getCurrentWorkspace(currentUser);

  const isAdmin =
    currentUser?.role === "sysAdmin" || currentUser?.role === "wsAdmin";

  // Number, not the raw route param: useUpdateUser invalidates
  // ["user", { id: <number> }], and a string id would never match it.
  const userId = Number(id);

  const { data: user, isPending, isError, error } = useGetUser(userId);

  // A non-numeric id is NaN, which useGetUser treats as no id at all and
  // never fetches — without this the page would sit on "Loading..." forever.
  if (!Number.isInteger(userId)) {
    return <Text c="red">User not found</Text>;
  }

  if (isPending) {
    return <Text>Loading...</Text>;
  }

  if (isError) {
    return <Text c="red">{error.message}</Text>;
  }

  return (
    <EditUser
      user={user}
      currentEventId={currentWorkspaceId?.eventId}
      isAdmin={isAdmin}
    />
  );
};

export const EditUser = ({ user, currentEventId, isAdmin, ticketId }) => {
  const defaultValues = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    occupation: user?.occupation || "",
    biography: user?.biography || "",
    discoverable: user?.discoverable || false,
    picture: user?.picture || "",
    isDataStorageConsentGiven: user?.isDataStorageConsentGiven || false,
  };

  const { mutate: updateUser, isPending: userUpdatePending } = useUpdateUser({
    id: user.id,
  });
  const { mutate: updateTicket, isPending: ticketUpdatePending } =
    useUpdateTicket({ ticketId });

  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields },
    reset,
  } = useForm({
    defaultValues,
    resolver: resolver(web_updateUserSchema, { formats: fullFormats }),
  });

  const onSubmit = (data) => {
    const dirtyData = extractDirtyData(data, dirtyFields);

    // If we're in ticket context and email is being changed, use ticket update
    if (ticketId && dirtyData.email) {
      const emailChange = { email: dirtyData.email };
      const otherChanges = { ...dirtyData };
      delete otherChanges.email;

      // Handle email change through ticket endpoint
      updateTicket(emailChange, {
        onError: () => {
          reset({ ...defaultValues });
        },
        onSuccess: () => {
          // After email change, handle other user data changes if any
          if (Object.keys(otherChanges).length > 0) {
            updateUser(otherChanges, {
              onError: () => {
                reset({ ...defaultValues });
              },
              onSuccess: () => {
                reset({ ...defaultValues, ...dirtyData });
              },
            });
          } else {
            reset({ ...defaultValues, ...dirtyData });
          }
        },
      });
    } else {
      // Regular user update
      updateUser(dirtyData, {
        onError: () => {
          reset({ ...defaultValues });
        },
        onSuccess: () => {
          reset({ ...defaultValues, ...dirtyData });
        },
      });
    }
  };

  const mutatePending = ticketId
    ? ticketUpdatePending || userUpdatePending
    : userUpdatePending;

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Fieldset legend="Personal information">
          <ImageUpload
            control={control}
            name="picture"
            label="Profile picture"
            defaultImage={user?.picture}
            resize
          />
          {user?.picture && (
            <DeleteProfilePicture userId={user?.id} my="md" size="sm" />
          )}
          <TextInput
            mt="md"
            name="firstName"
            control={control}
            placeholder="John"
            label="First name"
          />
          <TextInput
            mt="md"
            name="lastName"
            control={control}
            placeholder="Doe"
            label="Last name"
          />
          <TextInput
            mt="md"
            name="email"
            control={control}
            placeholder="john@doe.com"
            label="Email"
            description={
              ticketId
                ? "Changing email will reassign ticket to user with this email if they exist, or update current user's email"
                : "Use with caution, editing email will require re-verification"
            }
          />
          <TextInput
            mt="md"
            name="occupation"
            control={control}
            placeholder="Software Engineer @ RoundCube"
            label="Occupation and company"
          />
          <Textarea
            mt="md"
            name="biography"
            control={control}
            placeholder="Tell us about yourself"
            label="Biography"
          />
          <Checkbox
            mt="md"
            name="discoverable"
            control={control}
            label="Discoverable"
            description="Allow other users to find you by searching your name"
          />
          <Checkbox
            mt="md"
            name="isDataStorageConsentGiven"
            control={control}
            disabled={true}
            label="I consent to the processing of my profile data by this application in accordance with the privacy policy."
            description={
              <Paper shadow="md" p="sm" withBorder mt="xs">
                <Flex direction="row" align="center" gap={10}>
                  <IconAlertCircle />
                  <Text style={{ fontSize: 13 }}>
                    You are editing this profile as a data controller. Changes
                    made here are your responsibility under applicable data
                    protection regulations.
                  </Text>
                </Flex>
              </Paper>
            }
          />
        </Fieldset>
        <Button
          mt="md"
          type="submit"
          variant="light"
          disabled={!isDirty}
          loading={mutatePending}
        >
          Save changes
        </Button>
      </form>
      <Accordion variant="separated" style={{ marginTop: 20 }}>
        <Accordion.Item value="user-documents">
          <Accordion.Control>Documents</Accordion.Control>
          <Accordion.Panel>
            {isAdmin && (
              <UploadUserDocument userId={user?.id} eventId={currentEventId} />
            )}
            <UserDocuments userId={user?.id} eventId={currentEventId} />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Container>
  );
};

export default EditUserWrapper;
